import { State, Action, StateContext, Selector } from '@ngxs/store';
import { DashboardService } from './service';
import { IDashboardState } from './types';
import {
    ChangeTimesheetStatus,
    FetchCompanyTimesheets,
    FetchMoreCompanyTimesheets,
    SetAdminTimesheetsFilters,
    SetAppUsageFilters, SetMoreInfo,
    SetSearchField,
} from './actions';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ICompany, IPagination, IUser } from '../../../../modules/authorization/store/types';
import { IPayroll } from 'src/app/modules/logged/payrolls/store/types';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { PayrollService } from 'src/app/modules/logged/payrolls/store/service';

const defaults: IDashboardState = {
    isLoading: null,
    searchField: '',
    more_info: true,
    adminCompanyTimesheetsFilters: {
        visible: true,
        start_date: '',
        end_date: ''
    },
    adminCompanyTimesheets: null,
    appUsageFilters: {
        page: 1,
        results: 25,
        sort: 'createdDESC',
        webSubcontractor: false,
        iosSubcontractor: false,
        androidSubcontractor: false,
    },
    appUsage: null,
};

@State<IDashboardState>({
    name: 'dashboard',
    defaults
})
@Injectable()
export class DashboardState {

    @Selector()
    static moreInfo(state: IDashboardState) {
        return state.more_info;
    }

    @Selector()
    static isLoading(state: IDashboardState) {
        return state.isLoading;
    }

    @Selector()
    static searchField(state: IDashboardState) {
        return state.searchField;
    }

    @Selector()
    static adminCompanyTimesheetsFilters(state: IDashboardState) {
        return state.adminCompanyTimesheetsFilters;
    }

    @Selector()
    static adminCompanyTimesheets(state: IDashboardState) {
        return state.adminCompanyTimesheets;
    }

    @Selector()
    static appUsageFilters(state: IDashboardState) {
        return state.appUsageFilters;
    }

    @Selector()
    static appUsage(state: IDashboardState) {
        return state.appUsage;
    }

    constructor(
        private dashboardService: DashboardService,
        private payrollService: PayrollService,
    ) { }

    @Action(SetAdminTimesheetsFilters)
    SetAdminTimesheetsFilters(ctx: StateContext<IDashboardState>, action: SetAdminTimesheetsFilters) {
        ctx.patchState({
            adminCompanyTimesheetsFilters: action.filters,
            isLoading: true,
        });
        const state = ctx.getState();
        return this.dashboardService.fetchAllCompaniesForDashboard(state.adminCompanyTimesheetsFilters)
            .pipe(tap((data: ICompany[]) => ctx.patchState({ adminCompanyTimesheets: data, isLoading: false })));
    }

    @Action(SetMoreInfo)
    SetMoreInfo(ctx: StateContext<IDashboardState>, action: SetMoreInfo) {
        ctx.patchState({
            more_info: action.value
        });
    }

    @Action(SetAppUsageFilters)
    SetAppUsageFilters(ctx: StateContext<IDashboardState>, action: SetAppUsageFilters) {
        let state = ctx.getState();
        ctx.patchState({
            appUsageFilters: {
                ...state.appUsageFilters,
                ...action.filters
            },
            isLoading: true,
        });
        state = ctx.getState();
        return this.dashboardService.fetchAppUsage({ ...state.appUsageFilters, search: state.searchField })
            .pipe(tap((pagination: IPagination<IUser>) => ctx.patchState({ appUsage: pagination, isLoading: false })));
    }

    @Action(FetchCompanyTimesheets)
    FetchCompanyTimesheets(ctx: StateContext<IDashboardState>, action: FetchCompanyTimesheets) {
        const state = ctx.getState();
        const company = state.adminCompanyTimesheets.find((el: ICompany) => el._id === action.companyID);
        if (company.isExpanded) {
            ctx.setState(
                patch({
                    adminCompanyTimesheets: updateItem<ICompany>((el) => el._id === action.companyID, patch({ isExpanded: false })),
                })
            );
        } else {
            ctx.patchState({ isLoading: true });
            return this.dashboardService.fetchAllPayrollsFromCompanyForDashboard(action.companyID, { ...state.adminCompanyTimesheetsFilters, results: 10, sort: 'createdDESC' })
                .pipe(tap((data: IPagination<IPayroll>) => ctx.setState(
                    patch({
                        isLoading: false,
                        adminCompanyTimesheets: updateItem<ICompany>((el) => el._id === action.companyID, patch({ payrolls: data.results, page: 1, pages: data.pages, isExpanded: true })),
                    })
                )));
        }
    }

    @Action(FetchMoreCompanyTimesheets)
    FetchMoreCompanyTimesheets(ctx: StateContext<IDashboardState>, action: FetchMoreCompanyTimesheets) {
        const state = ctx.getState();
        const company = state.adminCompanyTimesheets.find((el: ICompany) => el._id === action.companyID);
        const page = company.page + 1;
        ctx.patchState({ isLoading: true });
        return this.dashboardService.fetchAllPayrollsFromCompanyForDashboard(action.companyID, { ...state.adminCompanyTimesheetsFilters, page, results: 10, sort: 'createdDESC' })
            .pipe(tap((data: IPagination<IPayroll>) => ctx.setState(
                patch({
                    isLoading: false,
                    adminCompanyTimesheets: updateItem<ICompany>((el) => el._id === action.companyID, patch({ payrolls: [...company.payrolls, ...data.results], page, pages: data.pages })),
                })
            )));
    }

    @Action(ChangeTimesheetStatus)
    ChangeTimesheetStatus(ctx: StateContext<IDashboardState>, action: ChangeTimesheetStatus) {
        let data: any;
        if (action.status === 'INVOICES_PAID') {
            data = { invoice_paid: true };
        } else if (action.status === 'SCHEDULED_FOR_PAYMENT') {
            data = { scheduled_for_payment: true };
        }
        else if (action.status === 'COMPLETED') {
            data = { invoice_completed: true };
        }
        return this.payrollService.update(action.payrollID, data);
    }

    @Action(SetSearchField)
    SetSearchField(ctx: StateContext<IDashboardState>, action: SetSearchField) {
        ctx.patchState({ searchField: action.value });
        ctx.dispatch(new SetAppUsageFilters({ page: 1 }));
    }

}
